import {css, StyleSheet} from 'aphrodite';
import React, {useState, ChangeEvent, useCallback, useMemo} from 'react';

import {SelectTemplateCollectionItemTreeTemplate} from 'notebook/entry-templates/CreateEntryFromTemplateModal/utils';
import SelectTemplateCollectionItemTree from 'notebook/entry-templates/SelectTemplateCollectionItemTree/SelectTemplateCollectionItemTree';
import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {STEP_NAME_PLACEHOLDER} from 'procedures/design/step-config/StepConfigConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import ErrorMessage from 'ui-library/ErrorMessage';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';

export enum StepPlacement {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

export enum FromType {
  NONE = 'NONE',
  TEMPLATE = 'TEMPLATE',
  SUBTEMPLATE = 'SUBTEMPLATE',
}

interface OnRequestSubmitParams {
  stepPlacement: StepPlacement;
  stepName: string;
  fromId?: string;
}

export interface AddStepModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (params: OnRequestSubmitParams) => void;
  stepGroupName: string;
  existingStepNames: Array<string>;
}

export function PlacementRadioButtons({
  stepPlacement,
  setStepPlacement,
  stepGroupName,
}: {
  stepPlacement: StepPlacement;
  setStepPlacement: (stepPlacement: StepPlacement) => void;
  stepGroupName: string;
}): JSX.Element {
  return (
    <div className={css(styles.section)}>
      <span className={css(styles.label)}>Placement</span>
      <div className={css(styles.input)}>
        <RadioButtonSelector
          hasBottomSpacing={true}
          selectedValue={stepPlacement}
          options={[
            {label: `Add to the top of ${stepGroupName}`, value: StepPlacement.TOP},
            {label: `Add to the bottom of ${stepGroupName}`, value: StepPlacement.BOTTOM},
          ]}
          onChange={(newValue: StepPlacement) => {
            setStepPlacement(newValue);
          }}
          isVertical={true}
        />
      </div>
    </div>
  );
}

export function FromTypeRadioButtons({
  fromType,
  setFromType,
}: {
  fromType: FromType;
  setFromType: (fromType: FromType) => void;
}): JSX.Element {
  return (
    <div className={css(styles.section)}>
      <span className={css(styles.label)}>Start from</span>
      <div className={css(styles.input)}>
        <RadioButtonSelector
          hasBottomSpacing={true}
          selectedValue={fromType}
          options={[
            {label: `Blank`, value: FromType.NONE},
            {label: `Entry template`, value: FromType.TEMPLATE},
            {label: `Sub-template`, value: FromType.SUBTEMPLATE},
          ]}
          onChange={(newValue: FromType) => {
            setFromType(newValue);
          }}
          isVertical={true}
        />
      </div>
    </div>
  );
}

export default function AddStepModal({
  onRequestClose,
  onRequestSubmit,
  stepGroupName,
  existingStepNames,
}: AddStepModalProps): JSX.Element {
  const [stepName, setStepName] = useState(() =>
    normalizeNameForUniqueness(STEP_NAME_PLACEHOLDER, existingStepNames, STEP_NAME_PLACEHOLDER)
  );
  const [stepPlacement, setStepPlacement] = useState(StepPlacement.BOTTOM);
  const [fromType, setFromType] = useState(FromType.NONE);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [subtemplateId, setSubtemplateId] = useState<string | null>('');

  const isDisabled = useMemo(
    () =>
      (!templateId && fromType === FromType.TEMPLATE) ||
      (!subtemplateId && fromType === FromType.SUBTEMPLATE) ||
      (errorMessage != null && errorMessage.length > 0) ||
      stepName.length === 0,
    [templateId, subtemplateId, fromType, stepName, errorMessage]
  );
  const onSubmit = useCallback(() => {
    const fromId: string | undefined =
      templateId && fromType === FromType.TEMPLATE
        ? templateId
        : subtemplateId && fromType === FromType.SUBTEMPLATE
          ? subtemplateId
          : undefined;
    onRequestSubmit({
      stepName,
      stepPlacement,
      fromId,
    });
  }, [templateId, fromType, subtemplateId, onRequestSubmit, stepName, stepPlacement]);

  const changeTemplate = (template: Pick<SelectTemplateCollectionItemTreeTemplate, 'id' | 'name'>): void => {
    setTemplateId(template.id);
    if (stepName.startsWith(STEP_NAME_PLACEHOLDER)) {
      const uniqueTemplateName = normalizeNameForUniqueness(
        template.name,
        existingStepNames,
        STEP_NAME_PLACEHOLDER
      );
      setStepName(uniqueTemplateName);
    }
  };

  return (
    <Modal
      title="Add step"
      onRequestClose={onRequestClose}
      onRequestSubmit={onSubmit}
      buttons={[
        {
          text: 'Add step',
          submit: true,
          dataTestAttrs: {component: 'AddStepModal', element: 'submitButton'},
          disabled: isDisabled,
        },
      ]}
    >
      <div>
        <div className={css(styles.section)}>
          <span className={css(styles.label)}>Step name</span>
          <TextInput
            className={css(styles.input)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newStepName = e.target.value;
              setErrorMessage(existingStepNames.includes(newStepName) ? 'Step names must be unique' : null);
              setStepName(newStepName);
            }}
            onFocus={(e) => {
              if (stepName.startsWith(STEP_NAME_PLACEHOLDER)) {
                e.target.select();
              }
            }}
            hasError={stepName.length === 0 || (errorMessage != null && errorMessage.length > 0)}
            autoFocus={true}
            value={stepName}
          />
          {errorMessage && <ErrorMessage error={errorMessage} />}
        </div>
        <PlacementRadioButtons
          stepPlacement={stepPlacement}
          setStepPlacement={setStepPlacement}
          stepGroupName={stepGroupName}
        />
        <FromTypeRadioButtons fromType={fromType} setFromType={setFromType} />
        {fromType === FromType.TEMPLATE && (
          <SelectTemplateCollectionItemTree
            collectibleType="TEMPLATE"
            collectibleId={templateId || ''}
            excludeTaskSchemaExecutionTemplates={true}
            setCollectible={(template) => {
              if (template) {
                changeTemplate(template);
              } else {
                setTemplateId(null);
              }
            }}
          />
        )}
        {fromType === FromType.SUBTEMPLATE && subtemplateId != null && (
          <SelectTemplateCollectionItemTree
            collectibleType="SUB-TEMPLATE"
            collectibleId={subtemplateId}
            setCollectible={(subtemplate) => {
              if (subtemplate) {
                setSubtemplateId(subtemplate.id);
              } else {
                setSubtemplateId('');
              }
            }}
          />
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: '12px',
    marginBottom: '12px',
  },
  label: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    lineHeight: '16px',
  },
  input: {
    marginTop: '4px',
  },
});
